/**
 * The environments this person can reach, and going to one —
 * `docs/identity-and-sign-in.md` §2.
 *
 * A hook rather than state in each frame, because both frames need exactly the
 * same three things and the office app and Rowted Admin differ only in which
 * request client they use. The list is fetched once per mount and cached by
 * the server for an hour, so opening the menu repeatedly costs nothing.
 *
 * **Switching is a redirect, not a fetch.** `POST /switch` answers a URL on the
 * target's own host; the browser goes there and the target mints its own
 * session. Doing it any other way would set the cookie at this host, where
 * `requireSession` refuses it on the very next request (§5.2).
 *
 * **The paths are relative and there is no base to pass.** Both routes are
 * `/me/environments` and `/me/switch` *within their own client*, because
 * `lib/api.ts` prefixes `/api/v1` and `lib/admin-api.ts` prefixes
 * `/api/v1/platform`. This used to take a `base`, and Rowted Admin passed
 * `/platform/me` — so every call went to `/api/v1/platform/platform/me/…`,
 * answered 401, and the catch below swallowed it. The Environments section
 * had therefore never once appeared in the console, which is what Aaron saw
 * and read as a consequence of his two accounts having different passwords:
 * "the user menu drop down doesn't have the switcher to the other
 * environment i'm in". The parameter is gone rather than corrected, because
 * a parameter whose only two callers want the same value is a place for this
 * to happen again.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@rowted/shared'
import type { MenuEnvironment } from './account-menu.js'

export interface EnvironmentsAnswer {
  environments: MenuEnvironment[]
  current: { kind: 'tenant' | 'platform'; tenantId?: string }
}

type Request = <T>(
  path: string, init?: { method?: 'POST'; body?: unknown },
) => Promise<T>

export function useEnvironments(request: Request): {
  environments: MenuEnvironment[]
  current: { kind: 'tenant' | 'platform'; tenantId?: string }
  switchTo: (target: MenuEnvironment) => Promise<void>
  /**
   * Why a switch did not happen.
   *
   * Held here rather than thrown at the caller because the menu has already
   * closed by then: the person pressed a row and nothing moved, and something
   * on screen has to say why. Both frames draw it as a toast.
   */
  error: string | null
  dismissError: () => void
  /** After a link is removed, so the list stops showing what it no longer is. */
  reload: () => void
} {
  const [answer, setAnswer] = useState<EnvironmentsAnswer>({
    environments: [],
    /*
     * Until the read lands, nothing is current — better than guessing
     * `tenant`, which would put the check on the wrong row for a moment in
     * Rowted Admin.
     */
    current: { kind: 'tenant' },
  })
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(() => {
    void request<EnvironmentsAnswer>('/me/environments')
      /*
       * Only a well-formed answer replaces the state. An answer without a list
       * would leave `environments` undefined, and every caller maps over it —
       * a menu that throws is worse than a menu with no Environments section.
       */
      .then((next) => { if (Array.isArray(next?.environments)) setAnswer(next) })
      .catch(() => { /* the menu simply has no Environments section */ })
  }, [request])
  useEffect(reload, [reload])

  const switchTo = useCallback(async (target: MenuEnvironment) => {
    setError(null)
    try {
      const asked = await request<{ url: string | null }>('/me/switch', {
        method: 'POST',
        body: { kind: target.kind, ...(target.tenantId ? { tenantId: target.tenantId } : {}) },
      })
      if (asked.url) window.location.href = asked.url
    } catch (caught) {
      setError(caught instanceof ApiError
        ? caught.message
        : 'That could not be opened. Try again.')
    }
  }, [request])

  return { ...answer, switchTo, error, dismissError: () => setError(null), reload }
}
